import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Image as ChakraImage,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    Heading,
    Icon,
    Input,
    List,
    ListIcon,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    Tooltip,
    useDisclosure,
    useToast,
    VStack
} from "@chakra-ui/react";

import { FaDownload } from "react-icons/fa"; // for the edit icon

import { saveAs } from "file-saver";
import JSZip from "jszip";
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { PAID_CUSTOMERS } from "../../../data/stats";

import { PiCheckCircleFill } from "react-icons/pi";
import {
    Container,
    MainSubheading,
    MainTitle,
    Slab
} from "../../common/index.js";

let modelReadyResolve;
let modelReadyReject;

// This promise lives outside the component to persist across renders
const modelReadyPromise = new Promise((resolve, reject) => {
    modelReadyResolve = resolve;
    modelReadyReject = reject;
});

export const RemoveBG = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const toast = useToast();

    // Track whether we are currently processing an image
    const [isProcessing, setIsProcessing] = useState(false);

    // Keep track of all images in an array with relevant info
    // Each item: { id, file, originalUrl, bgRemovedUrl, status }
    const [images, setImages] = useState([]);

    const [isDownloading, setIsDownloading] = useState(false); // Download loading state

    // --- MODEL LOADING LOGIC (unchanged) ---
    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleScriptsLoaded = async () => {
            try {
                const initialized = await window.initializeModel("bg_removal");
                if (initialized) {
                    console.log("Model initialized successfully!");
                    modelReadyResolve();
                }
            } catch (err) {
                console.error("Error initializing model");
                console.log(err);
                setError("Failed to initialize model");
                modelReadyReject();
            }
            setIsLoading(false);
        };

        document.addEventListener("modelScriptsLoaded", handleScriptsLoaded);
        return () => {
            document.removeEventListener("modelScriptsLoaded", handleScriptsLoaded);
        };
    }, []);

    // --- SEPARATE EFFECT TO PROCESS IMAGES ONE BY ONE ---
    useEffect(() => {
        // If we are already processing, do nothing
        if (isProcessing) return;

        // Find the first queued image
        const nextQueued = images.find((img) => img.status === "queued");
        if (!nextQueued) return;

        // We have an image to process, so mark that we are busy
        setIsProcessing(true);

        // Define an async function to run the process logic
        const processImage = async (queuedImg) => {
            try {
                // Mark image as processing
                setImages((prevImages) =>
                    prevImages.map((img) =>
                        img.id === queuedImg.id ? { ...img, status: "processing" } : img
                    )
                );

                // Wait for the model to be ready
                await modelReadyPromise;

                // Process the file
                const results = await window.processImages([queuedImg.file]);
                const processedFile = results[0];
                const bgRemovedUrl = URL.createObjectURL(processedFile);
                // const bgRemovedUrl = "https://ik.imagekit.io/ikmedia/blue-bmw.jpg?tr=e-removedotbg";

                // Mark the image as done
                setImages((prevImages) =>
                    prevImages.map((img) =>
                        img.id === queuedImg.id
                            ? { ...img, bgRemovedUrl, status: "done" }
                            : img
                    )
                );
            } catch (err) {
                console.error("Error removing background:", err);
                // Mark the image as error
                setImages((prevImages) =>
                    prevImages.map((img) =>
                        img.id === queuedImg.id
                            ? { ...img, status: "error" }
                            : img
                    )
                );
                toast({
                    title: "Processing Error",
                    description: "Failed to remove background for one or more images.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                    position: "top-right",
                });
            } finally {
                // Release the lock so we can process the next image
                setIsProcessing(false);
            }
        };

        // Actually call our process function for the next queued image
        processImage(nextQueued);
    }, [images, isProcessing, toast]);

    // --- HANDLE FILES (NOW JUST QUEUES THEM) ---
    const handleFiles = async (fileList) => {
        if (error) {
            toast({
                title: "Model not initialized",
                description: "Failed to initialize model. Please refresh and try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        const validImages = [];
        let hasInvalidFile = false;

        // Validate each file
        for (const file of fileList) {
            if (file.type.startsWith("image/")) {
                // Check size limit (example: 10 MB)
                if (file.size <= 10 * 1024 * 1024) {
                    validImages.push(file);
                } else {
                    hasInvalidFile = true;
                }
            } else {
                hasInvalidFile = true;
            }
        }

        // Show an error toast if we encountered invalid files
        if (hasInvalidFile) {
            toast({
                title: "Invalid file type or size",
                description: "Only images up to 10MB are allowed",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        }

        // For each valid image, add it to the queue with status=queued
        validImages.forEach((file) => {
            const id = Math.random().toString(36).substring(2, 15);
            const originalUrl = URL.createObjectURL(file);

            setImages((prev) => [
                ...prev,
                {
                    id,
                    file,
                    originalUrl,
                    bgRemovedUrl: null,
                    editedImageUrl: null,
                    status: "queued", // <-- Initially "queued"
                },
            ]);
        });
    };

    // --- DRAG & DROP / PASTE LOGIC ---
    const onDrop = useCallback((event) => {
        event.preventDefault();
        if (event.dataTransfer?.files?.length > 0) {
            handleFiles(event.dataTransfer.files);
        }
    }, []);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
    }, []);

    const onPaste = useCallback((event) => {
        const { items } = event.clipboardData;
        const files = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith("image/")) {
                const file = item.getAsFile();
                if (file) files.push(file);
            }
        }
        if (files.length > 0) {
            handleFiles(files);
        }
    }, []);

    useEffect(() => {
        window.addEventListener("drop", onDrop);
        window.addEventListener("dragover", onDragOver);
        window.addEventListener("paste", onPaste);
        return () => {
            window.removeEventListener("drop", onDrop);
            window.removeEventListener("dragover", onDragOver);
            window.removeEventListener("paste", onPaste);
        };
    }, [onDrop, onDragOver, onPaste]);

    // Handle manual file selection
    const handleManualFilePicker = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const handleDownloadAllAsZip = async () => {
        try {
            const zip = new JSZip();

            // Add each processed (or queued) image to the zip
            // You can also filter only the "done" images if you prefer
            const doneImages = images.filter(
                (img) => img.status === "done"
            );

            setIsDownloading(true);

            for (const img of doneImages) {
                // If bgRemovedUrl exists, use that. Otherwise, fallback to the original
                const url = img.bgRemovedUrl || img.originalUrl;
                const response = await fetch(url);
                const blob = await response.blob();

                // Use original file name or append a suffix
                const fileName = img.file.name;
                zip.file(fileName, blob);
            }

            // Generate the zip and download
            const content = await zip.generateAsync({ type: "blob" });
            setIsDownloading(false);
            saveAs(content, "images.zip");
        } catch (err) {
            console.error("Error creating zip:", err);
            toast({
                title: "Error",
                description: "Failed to create ZIP file.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        }
    };

    // Determine how to display statuses
    const displayStatus = (status) => {
        if (isLoading && !error) return "Loading model...";
        if (status === "queued") return "Queued...";
        if (status === "processing") return "Removing background...";
        if (status === "done") return "Background removed";
        if (status === "error") return "Error";
    };

    return (
        <Box>
            <Scripts />
            <Slab>
                <Container>
                    <HeroSection handleManualFilePicker={handleManualFilePicker} />

                    {images.length > 0 && (
                        <>
                            <Alert status="info" borderRadius="md" bg="blue.50" color="blue.800" mt={8}>
                                <AlertIcon />
                                <Box>
                                    <Text>
                                        Background removal requires significant processing power, so we handle one image at a time directly in your browser for optimal performance. For even better speed and output quality, consider real-time, URL-based AI transformations—including background removal, drop shadows, retouching, upscaling, and more. Over  {PAID_CUSTOMERS} companies process billions of media assets daily with ImageKit. {" "}
                                        <a
                                            href="https://imagekit.io/solutions/how-imagekit-uses-ai/"
                                            target="_blank"
                                            style={{ textDecoration: "underline" }}
                                        >
                                            Learn more
                                        </a>.
                                    </Text>
                                </Box>
                            </Alert>

                            {error && (
                                <Alert status="error" mt={8} borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        <Text>
                                            Error initializing model. Please refresh the page and try again.
                                        </Text>
                                    </Box>
                                </Alert>
                            )}
                            <Box mt={8} w={"100%"}>
                                <Button
                                    size="sm"
                                    colorScheme="green"
                                    isLoading={isDownloading}
                                    leftIcon={<FaDownload />}
                                    onClick={handleDownloadAllAsZip}
                                    isDisabled={images.filter((img) => img.status === "done").length === 0}
                                >
                                    Download processed images ({images.filter((img) => img.status === "done").length})
                                </Button>
                            </Box>

                            <Box py={2} w={"100%"}>
                                <Divider />
                            </Box>

                            <Box w={"100%"}>
                                <Grid templateColumns={{ base: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }} gap="4">
                                    {images.map((img) => (
                                        <ImagePreview
                                            key={img.id}
                                            src={img.bgRemovedUrl || img.originalUrl}
                                            name={img.file.name}
                                            status={img.status}
                                            displayStatus={displayStatus(img.status)}
                                            editedImageUrl={img.editedImageUrl}
                                            onUpdateSrc={(editedImageUrl) => {
                                                setImages((prev) =>
                                                    prev.map((item) =>
                                                        item.id === img.id
                                                            ? { ...item, editedImageUrl }
                                                            : item
                                                    )
                                                );
                                            }}
                                        />
                                    ))}
                                </Grid>
                            </Box>
                        </>
                    )}
                </Container>
            </Slab>
        </Box>
    );
};


const ImagePreview = ({ src, name, displayStatus, status, onUpdateSrc }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    // Download processed image
    const handleDownload = (src, fileName) => {
        const link = document.createElement("a");
        link.href = src;
        link.download = fileName || "processed_image.png";
        link.click();
    };

    // When user finishes editing, we get back finalSrc
    const handleEditDone = (finalSrc) => {
        // Update the src in parent’s state
        onUpdateSrc(finalSrc);
    };

    return (
        <>
            <Box
                position="relative"
                overflow="hidden"
                borderRadius="lg"
                boxShadow="md"
                bg="gray.100"
                _hover={{ boxShadow: "lg" }}
                p={4}
            >
                <VStack spacing={2} align={"left"}>
                    <Box>
                        <Tooltip label={name} aria-label="File name tooltip">
                            <Text isTruncated fontSize="sm" fontWeight="semibold">
                                {name}
                            </Text>
                        </Tooltip>
                    </Box>

                    {/* The main preview */}
                    <Box bg="white">
                        <Box
                            bgImage={`url('data:image/svg+xml;utf8,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><rect width="10" height="10" fill="%23eee"/><rect x="10" y="10" width="10" height="10" fill="%23eee"/></svg>')`}
                            bgRepeat="repeat"
                            bgSize="20px 20px"
                            h="200px"
                            display="flex"
                            justifyContent="center"
                            alignItems="end"
                        >
                            <ChakraImage
                                src={src}
                                alt={name}
                                h="100%"
                                w="100%"
                                objectFit="contain"
                            />
                        </Box>
                    </Box>

                    {/* Display status and download/edit actions */}
                    <Box>
                        <Text fontSize="sm" color="gray.500">
                            {displayStatus}
                        </Text>

                        {status === "done" && (
                            <Flex align="center" justify="space-between" mt={2}>
                                <Text
                                    fontSize="sm"
                                    color="imagekitBlue.600"
                                    cursor="pointer"
                                    onClick={() => handleDownload(src, name)}
                                    display="inline-block"
                                >
                                    <Icon as={FaDownload} mr={1} />
                                    Download
                                </Text>

                                {/* <IconButton
                                    aria-label="Edit Image"
                                    icon={<FaEdit />}
                                    variant="ghost"
                                    size="sm"
                                    colorScheme="blue"
                                    onClick={onOpen}
                                /> */}
                            </Flex>
                        )}
                    </Box>
                </VStack>
            </Box>

            {/* Pass onDone callback to the modal */}
            <EditImageModal
                isOpen={isOpen}
                onClose={onClose}
                src={src}
                fileName={name}
                onDone={handleEditDone} // new callback
            />
        </>
    );
};
const EditImageModal = ({ isOpen, onClose, src, fileName, onDone }) => {
    const toast = useToast();

    const [bgColor, setBgColor] = useState("#ffffff");
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);
    const [useBlur, setUseBlur] = useState(false);
    const [blurValue, setBlurValue] = useState(5);

    // We store object URLs for the background image so we can preview it
    const backgroundImageUrl = backgroundImageFile
        ? URL.createObjectURL(backgroundImageFile)
        : null;

    // --- Handle background image selection ---
    const handleBackgroundImage = (e) => {
        if (e.target.files && e.target.files[0]) {
            setBackgroundImageFile(e.target.files[0]);
        }
    };

    // --- The main function that composites everything on a canvas ---
    const handleApply = async () => {
        try {
            // 1. Create a canvas and get its 2D context
            //    In a real setup, you might match the size of the foreground image,
            //    or let the user pick a size. For this example, we’ll do 600x600
            //    or auto-size based on the first image loaded below.
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // 2. We need to load images as <img> objects
            const [foregroundImg, backgroundImg] = await loadImages(
                src,
                backgroundImageUrl
            );

            // Decide the final dimensions
            // If we have a background image, use that dimension. Otherwise, use the foreground image dimension
            const width = foregroundImg.naturalWidth;
            const height = foregroundImg.naturalHeight;

            canvas.width = width;
            canvas.height = height;

            // 3. Draw background (color or image)
            if (backgroundImg) {
                // If user wants blur, we can use the Canvas 2D `filter` property (supported in modern browsers)
                if (useBlur) {
                    ctx.filter = `blur(${blurValue}px)`;
                }
                ctx.drawImage(backgroundImg, 0, 0, width, height);

                // Reset filter for next draw calls
                ctx.filter = "none";
            } else {
                // No background image => fill with color
                // If user wants blur and no background image, that doesn't do anything visible
                // (We can't "blur" a solid color in a meaningful way),
                // but let's just fill with the color anyway.
                ctx.fillStyle = bgColor;
                ctx.fillRect(0, 0, width, height);
            }

            // 4. If user chose no background image, we overlay the color with blur
            //    There's no real “blur the color fill” effect. But you can do this:
            //    (a) fill canvas with color on an offscreen canvas,
            //    (b) apply your blur function, (c) draw that onto main canvas.
            //    For simplicity, we’ll skip it. If you want more advanced logic,
            //    you can integrate a library like stackblur-canvas.
            //    So if backgroundImg is null, we have just a color fill.

            // 5. Draw the foreground PNG (the image with background removed) on top
            //    (No blur filter applied to the foreground)
            ctx.drawImage(foregroundImg, 0, 0, width, height);

            canvas.toBlob((blob) => {
                if (!blob) {
                    return;
                }

                // 7. Convert the Blob to a Blob URL
                const finalSrc = URL.createObjectURL(blob);
                // Fire callback so that parent can do something with finalSrc
                onDone?.(finalSrc);

                // 8. Close the modal
                onClose();
            }, "image/png");
        } catch (err) {
            console.error("Error applying image edits:", err);
            toast({
                title: "Error",
                description: "Something went wrong while applying edits.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        }
    };

    return (
        <Modal size="xl" isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Customize Your Background</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {/* Live Preview (CSS-based) */}
                    <Box
                        position="relative"
                        mb={4}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        overflow="hidden"
                        w="100%"
                    >
                        <Box
                            bgImage={`url('data:image/svg+xml;utf8,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><rect width="10" height="10" fill="%23eee"/><rect x="10" y="10" width="10" height="10" fill="%23eee"/></svg>')`}
                            bgRepeat="repeat"
                            bgSize="20px 20px"
                            h="200px"
                            display="flex"
                            justifyContent="center"
                            alignItems="end"
                            position="relative"
                            bg={bgColor}
                            overflow="hidden"
                        >
                            {/* If user picked a background image, show it behind (with optional blur) */}
                            {backgroundImageUrl ? (
                                <Box
                                    as="img"
                                    src={backgroundImageUrl}
                                    alt="Background preview"
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    width="100%"
                                    height="100%"
                                    objectFit="cover"
                                    filter={useBlur ? `blur(${blurValue}px)` : "none"}
                                />
                            ) : (
                                // No background image => just color fill
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    width="100%"
                                    height="100%"
                                    bg={bgColor}
                                />
                            )}

                            {/* Foreground PNG on top */}
                            <ChakraImage
                                src={src}
                                alt={fileName}
                                zIndex={1}
                                maxH="100%"
                                maxW="100%"
                                objectFit="contain"
                                position="relative"
                            />
                        </Box>
                    </Box>

                    {/* Background color picker */}
                    {!backgroundImageUrl && (
                        <FormControl mb={4}>
                            <FormLabel>Background Color</FormLabel>
                            <Input
                                type="color"
                                value={bgColor}
                                onChange={(e) => setBgColor(e.target.value)}
                            />
                        </FormControl>
                    )}

                    {/* Background image upload */}
                    <FormControl mb={4}>
                        <FormLabel>Optional Background Image</FormLabel>
                        <Input type="file" accept="image/*" onChange={handleBackgroundImage} />
                    </FormControl>

                    {/* Blur checkbox + slider */}
                    <FormControl display="flex" alignItems="center">
                        <Checkbox
                            isChecked={useBlur}
                            onChange={(e) => setUseBlur(e.target.checked)}
                            mr={4}
                        >
                            Blur Background
                        </Checkbox>
                        {useBlur && (
                            <Box flex={1}>
                                <Slider
                                    aria-label="blur-slider"
                                    value={blurValue}
                                    onChange={setBlurValue}
                                    min={0}
                                    max={20}
                                >
                                    <SliderTrack>
                                        <SliderFilledTrack />
                                    </SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </Box>
                        )}
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="blue" onClick={handleApply}>
                        Apply
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

async function loadImages(foregroundSrc, backgroundSrc) {
    const loadSingleImage = (src) =>
        new Promise((resolve, reject) => {
            if (!src) return resolve(null);
            const img = new Image();
            img.crossOrigin = "anonymous"; // if needed
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });

    const [fg, bg] = await Promise.all([
        loadSingleImage(foregroundSrc),
        loadSingleImage(backgroundSrc),
    ]);

    return [fg, bg];
}

const HeroSection = ({ handleManualFilePicker }) => {
    return (
        <Grid templateColumns={{
            base: "1fr",
            md: "1fr 1fr"
        }} gap={8}>
            <Box background="imagekitGray.50" borderRadius="24px">
                <label
                    htmlFor="imageUpload"
                    style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        cursor: "pointer"
                    }}
                >
                    <Flex
                        direction={"column"}
                        w="100%"
                        height={"100%"}
                        border="dashed 3px #cdd5df"
                        borderRadius="24px"
                        alignItems={"center"}
                        justifyContent={"center"}
                        cursor={"pointer"}
                    >
                        <input id="imageUpload" style={{ display: "none" }} type="file" accept="image/*" multiple onChange={handleManualFilePicker} />
                        <Heading fontSize={"3xl"} as="h2" color="#4f4f4f" fontWeight="600" textAlign={"center"} lineHeight="1.5">
                            Drop, paste or<br />
                            <Text as="span" color="imagekitBlue.600">choose file</Text>
                        </Heading>
                        <Text as="p" color="imagekitGray.600" textAlign={"center"} lineHeight="1.5">

                        </Text>
                    </Flex>
                </label>
            </Box>
            <Box>
                {/* <FaEraser /> */}
                <ChakraImage height={"50px"} src="https://ik.imagekit.io/ikmedia/ai-magic-icon.svg" />
                <MainTitle as="h1" left>
                    Remove background from images
                </MainTitle>
                <MainSubheading
                    left
                    style={{ marginBottom: "24px", marginTop: "12px" }}
                >
                    Automatically remove background from images in seconds
                </MainSubheading>
                <List spacing={3}>
                    <ListItem>
                        <ListIcon as={PiCheckCircleFill} color="imagekitBlue.500" boxSize={6} />
                        High-quality results & HD exports
                    </ListItem>
                    <ListItem>
                        <ListIcon as={PiCheckCircleFill} color="imagekitBlue.500" boxSize={6} />
                        Browser-based, local processing
                    </ListItem>
                    <ListItem>
                        <ListIcon as={PiCheckCircleFill} color="imagekitBlue.500" boxSize={6} />
                        Unlimited usage, no watermarks
                    </ListItem>
                    <ListItem>
                        <ListIcon as={PiCheckCircleFill} color="imagekitBlue.500" boxSize={6} />
                        Ad-free experience
                    </ListItem>
                </List>
            </Box>
        </Grid>
    )
}

const Scripts = () => {
    return <Helmet>
        <script>
            {`
        window.scriptLoaded = function() {
          document.dispatchEvent(new Event("modelScriptsLoaded"));
        };
      `}
        </script>
        <script src="/process.js" async onload="scriptLoaded();" />
    </Helmet>
}
